import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = getRecentArticles(4).filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">该文章不存在或已被移除</p>
            <Link to="/articles" className="text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer">返回文章列表</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const seoTitle = `${article.title} - 蜜芽育儿`;
  const seoDesc = article.excerpt.substring(0, 150);
  const seoKeywords = `蜜芽育儿,${article.category},母婴知识,育儿指南`;
  const canonicalUrl = `https://childcart.github.io/article/${slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    publisher: { '@type': 'Organization', name: '蜜芽', url: 'https://childcart.github.io' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
  };

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '育儿文章', href: '/articles' },
              { label: article.title },
            ]}
          />

          <article className="px-4 md:px-6 pb-16">
            <div className="relative aspect-[21/9] rounded-2xl overflow-hidden mb-8 bg-background-200">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="bg-accent-100 text-accent-700 px-3 py-1 rounded-full text-xs font-medium">
                {article.category}
              </span>
              <span>{article.date}</span>
              <span>{article.readTime}阅读</span>
              <span>{article.author}</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-8 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h3:text-foreground-950 prose-h3:font-bold prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-4
                prose-p:mb-4 prose-p:leading-relaxed
                prose-strong:text-primary-600"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200 mt-12 pt-8">
              <h3 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group flex gap-3 p-3 rounded-xl bg-background-100 hover:bg-background-200 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-background-300">
                      <img src={ra.image} alt={ra.title} className="w-full h-full object-cover" title={ra.title} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground-500 mb-1">{ra.date}</p>
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors duration-200">
                        {ra.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}